import { Component, input } from '@angular/core';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { ValidationErrors } from '@angular/forms';
import { IconComponent } from '../../icon/icon.component';

@Component({
  selector: 'ds-field-error-message',
  imports: [CommonModule, IconComponent],
  templateUrl: './field-error-message.component.html',
  styleUrls: ['./field-error-message.component.scss'],
})
export class FieldErrorMessageComponent {
  public id = input<string>();
  public label = input<string | undefined>('');
  public errors = input<ValidationErrors | null | undefined>();
  public customErrorMessage = input<string | undefined>();

  private errorMessages: { [key: string]: (label: string) => string } = {
    required: (label) => $localize`:@@fieldValidationRequired:${label} is verplicht`,
    requiredTrue: (label) => $localize`:@@fieldValidationRequiredTrue:${label} moet geaccepteerd worden`,
    min: (label) => $localize`:@@fieldValidationMin:${label} is lager dan minimimum waarde`,
    max: (label) => $localize`:@@fieldValidationMax:${label} is hoger dan maximum waarde`,
    email: (label) => $localize`:@@fieldValidationEmail:${label} is ongeldig`,
    pattern: (label) => $localize`:@@fieldValidationPattern:${label} is ongeldig`,
    minlength: (label) =>
      $localize`:@@fieldValidationMinLength:Minimale lengte voor ${label} is ${this.errors()!['minlength']['requiredLength']}`,
    maxlength: (label) =>
      $localize`:@@fieldValidationMaxLength:Maximale lengte voor ${label} is ${this.errors()!['maxlength']['requiredLength']}`,
    uppercase: (label) => $localize`:@@fieldValidationUppercase:${label} moet minstens één hoofdletter bevatten (A–Z).`,
    lowercase: (label) =>
      $localize`:@@fieldValidationLowercase:${label} moet minstens één kleine letter bevatten (a–z).`,
    digit: (label) => $localize`:@@fieldValidationDigit:${label} moet minstens één cijfer bevatten (0–9).`,
    special: (label) =>
      $localize`:@@fieldValidationSpecial:${label} moet minstens één speciaal teken bevatten (bijv. !@#$%).`,
    null: (label) => $localize`:@@fieldValidationNull:${label} moet leeg zijn`,
    maximumDate: (label) =>
      $localize`:@@fieldValidationMaximumDate:${label} moet ${this.errors()!['maximumDate']['requiredDate']} of eerder zijn`,
    minimumDate: (label) =>
      $localize`:@@fieldValidationMinimumDate:${label} moet ${this.errors()!['minimumDate']['requiredDate']} of later zijn`,
    minimumAge: () => $localize`:@@fieldValidationMinimumAge:Leeftijd moet minimaal 18 jaar zijn`,
    fileSizeExceeded: () =>
      $localize`:@@fieldValidationFileSizeExceeded:Dit bestand is te groot (max. ${this.errors()!['fileSizeExceeded']['maxFileSizeInMb']} MB)`,
    coRequired: () =>
      $localize`:@@fieldValidationRequiredWithGroup:Je kunt de velden van ${this.errors()!['coRequired']['groupName']} niet gedeeltelijk invullen.`,
    passwordNotStrongEnough: () => this.errors()!['passwordNotStrongEnough']['errorMessage'],
    endTimeBeforeStartTime: () =>
      $localize`:@@fieldValidationEndTimeBeforeStartTime:Eindtijd moet later zijn dan starttijd.`,
    numberValidator: (label) => $localize`:@@fieldValidationValidNumber:${label} moet een numerieke waarde zijn`,
    positiveNumberValidator: (label) =>
      $localize`:@@fieldValidationValidNumber:${label} moet een numerieke waarde zijn groter of gelijk aan 0`,
    iban_countrycode: (label) => $localize`:@@fieldValidationValidIbanCountry:${label} bevat geen geldige landcode`,
    iban_length: (label) => $localize`:@@fieldValidationValidIban:${label} bevat geen geldige waarde`,
    iban_format: (label) => $localize`:@@fieldValidationValidIban:${label} bevat geen geldige waarde`,
    iban_mod97: (label) => $localize`:@@fieldValidationValidIban:${label} bevat geen geldige waarde`,
  };

  public getErrorMessages(): string[] {
    const titleCasePipe = new TitleCasePipe();
    const label = this.label() || 'Veld';
    const defaultMessage = $localize`:@@defaultFieldValidationInvalid:Ongeldige waarde`;

    if (!this.errors()) return [];

    const errorMessages: string[] = [];
    Object.keys(this.errors()!).forEach((keyError) => {
      const errorMessageFn = this.errorMessages[keyError];
      let errorMessage: string;
      if (this.customErrorMessage()) {
        errorMessage = this.customErrorMessage()!;
      } else if (errorMessageFn) {
        errorMessage = errorMessageFn(titleCasePipe.transform(label));
      } else {
        errorMessage = this.errors()![keyError]['message'] || defaultMessage;
      }
      errorMessages.push(errorMessage);
    });

    return [...new Set(errorMessages)];
  }
}
